import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, Lock, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { apiError, money2, dateShort } from "@/lib/api";
import SquareCheckout from "@/components/SquareCheckout";
import ClientInvoiceBlocks from "@/components/ClientInvoiceBlocks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pub = axios.create({ baseURL: API });

/** The client's own invoice page: one invoice, no login, nothing else reachable from here. */
export default function PublicInvoice() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [done, setDone] = useState(null);

  const load = async () => {
    try {
      const { data } = await pub.get(`/public/invoices/${token}`);
      setInv(data);
    } catch (e) {
      setError(apiError(e));
    }
  };

  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadPdf = async () => {
    try {
      const { data } = await pub.get(`/public/invoices/${token}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Invoice-${inv.number}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (error)
    return (
      <div className="flex min-h-screen items-center justify-center bg-bone px-6" data-testid="public-invoice-error">
        <div className="max-w-md rounded-sm bg-white p-10 text-center">
          <h1 className="font-serif text-2xl text-ink">This link isn’t available</h1>
          <p className="mt-3 text-sm text-stone">{error}</p>
        </div>
      </div>
    );

  if (!inv)
    return (
      <div className="flex min-h-screen items-center justify-center bg-bone text-sm text-fog">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} /> Loading your invoice…
      </div>
    );

  const studio = inv.studio || {};

  return (
    <div className="min-h-screen bg-bone px-5 py-10 sm:px-8 sm:py-16" data-testid="public-invoice-page">
      <div className="mx-auto max-w-3xl space-y-8">
        <header className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-fog">{studio.business_name || "Invoice"}</p>
            <h1 className="mt-3 font-serif text-3xl tracking-tight text-ink sm:text-4xl" data-testid="public-invoice-title">
              {inv.title}
            </h1>
            <p className="mt-2 text-sm text-stone">
              Invoice <span className="num">{inv.number}</span> · {inv.project_name}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[11px] uppercase tracking-[0.18em] text-fog">Balance due</p>
            <p className="num mt-2 text-3xl text-ink" data-testid="public-invoice-balance">{money2(inv.balance)}</p>
            <p className="mt-1 text-xs text-fog">{inv.due_date ? `Due ${dateShort(inv.due_date)}` : "Due on receipt"}</p>
          </div>
        </header>

        {done ? (
          <div className="rounded-sm bg-white p-10 text-center" data-testid="public-payment-success">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#EDEFC4]">
              <Check className="h-7 w-7 text-lime-dark" strokeWidth={1.5} />
            </span>
            <h2 className="mt-6 font-serif text-2xl text-ink">Thank you — payment received.</h2>
            <p className="mt-3 text-sm text-stone">
              {money2(done.amount)} has been charged
              {done.card_brand ? ` to your ${done.card_brand}${done.last_4 ? ` ending ${done.last_4}` : ""}` : ""}.
              {(done.invoice?.balance ?? inv.balance) > 0
                ? ` Your remaining balance is ${money2(done.invoice?.balance ?? inv.balance)}.`
                : " This invoice is now paid in full."}
            </p>
            <button data-testid="public-payment-done" onClick={() => { setDone(null); load(); }}
              className="mt-8 rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">
              Back to the invoice
            </button>
          </div>
        ) : (
          <>
            <section className="rounded-sm bg-white p-6 sm:p-9" data-testid="public-invoice-detail">
              <ClientInvoiceBlocks invoice={inv} testPrefix="public-block" />
              {inv.notes && <p className="mt-6 text-xs leading-relaxed text-stone">{inv.notes}</p>}
              <div className="mt-8 flex flex-wrap gap-3">
                {inv.payable && !paying && (
                  <button data-testid="public-pay-btn" onClick={() => setPaying(true)}
                    className="rounded-sm bg-lime px-6 py-3 text-sm font-medium text-ink transition-colors hover:bg-lime-dark">
                    Pay {money2(inv.balance)}
                  </button>
                )}
                <button data-testid="public-pdf-btn" onClick={downloadPdf}
                  className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-5 py-3 text-sm text-ink transition-colors hover:border-lime">
                  <Download className="h-4 w-4" strokeWidth={1.5} /> Download PDF
                </button>
              </div>
            </section>

            {paying && (
              <section data-testid="public-pay-panel">
                <SquareCheckout
                  invoice={{ id: inv.id, number: inv.number, client_name: inv.client_name }}
                  amount={Number(inv.balance)}
                  configPath={`/public/invoices/${token}/payment-config`}
                  payPath={`/public/invoices/${token}/pay`}
                  onPaid={(payment) => { setPaying(false); setDone(payment); }}
                />
              </section>
            )}

            {!!(inv.payments || []).length && (
              <section className="rounded-sm bg-white p-6 sm:p-9" data-testid="public-payment-history">
                <p className="text-[11px] uppercase tracking-[0.18em] text-fog">Payments received</p>
                <ul className="mt-4 divide-y divide-line text-sm">
                  {inv.payments.map((p, i) => (
                    <li key={i} className="flex flex-wrap items-center justify-between gap-3 py-3">
                      <span className="text-stone">
                        {dateShort(p.created_at)} · {p.card_brand || p.method || "Card"}
                        {p.last_4 ? ` ending ${p.last_4}` : ""}
                        {p.refunded_amount > 0 ? ` · ${money2(p.refunded_amount)} refunded` : ""}
                      </span>
                      <span className="num text-ink">{money2(p.amount)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}

        <footer className="flex flex-wrap items-center justify-between gap-3 pt-2 text-xs text-fog">
          <span className="inline-flex items-center gap-1.5">
            <Lock className="h-3 w-3" strokeWidth={1.5} /> Payments are processed securely by Square
          </span>
          <span>
            {studio.business_name}
            {studio.email ? ` · ${studio.email}` : ""}
          </span>
        </footer>
      </div>
    </div>
  );
}
